import { ContextualMenuItemType, IContextualMenuItem } from '@fluentui/react';
import { useLocalStorageState } from '@msrceng/component';
import { getDayAfterPreviousUpdateTuesday } from '../../../../../../util/date/getDayAfterPreviousUpdateTuesday';
import { DateRangeEnd, DateSelectorType } from './enums';
import { PickerActionBar } from './PickerActionBar';
import { PickerErrorBar } from './PickerErrorBar';
import { DateRangeSelector } from './selector/DateRangeSelector';
import { useDateSelectorEditorLogic } from './logic/useDateSelectorEditorLogic';
import { IDateRangeState } from '../../../../../../api/apiTypes';
import { useTranslation } from 'react-i18next';
import { useDateSelectorItemClearFunction } from './useDateSelectorItemClearFunction';

/**
 * Builds the (somewhat complex) menu for the date picker within a
 * Fluent Contextual Menu item list.
 */
export const useDateSelectorItems = ({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  onSave,
}: {
  onSave: () => void;
} & IDateRangeState): IContextualMenuItem[] => {
  const [t] = useTranslation(['search', 'translation']);
  const {
    hasChanges,
    isEndBeforeStart,
    isAnyDateAfterToday,
    isMoreThanOneYear,
    isStartDateValid,
    isEndDateValid,
  } = useDateSelectorEditorLogic({ startDate, endDate });

  //I don't like keeping this state this high, but the reset fn needs it.
  const [startType, setStartType] = useLocalStorageState<
    DateSelectorType | undefined
  >(`date-picker-selection-${DateRangeEnd.start}`, DateSelectorType.month);
  const [endType, setEndType] = useLocalStorageState<
    DateSelectorType | undefined
  >(`date-picker-selection-${DateRangeEnd.end}`, DateSelectorType.today);

  /** Reset data to source data */
  const clear = useDateSelectorItemClearFunction(
    startDate,
    setStartDate,
    startType,
    setStartType,
    endDate,
    setEndDate,
    endType,
    setEndType
  );

  /**
   * Reset data to the default cycle.
   *
   * The default cycle starts with the day after the previous update tuesday
   * and ends with today.
   */
  const resetToDefault = () => {
    setStartType(DateSelectorType.month);
    setEndType(DateSelectorType.today);

    setStartDate(getDayAfterPreviousUpdateTuesday());
    setEndDate(new Date());
  };

  return [
    {
      key: 'startdate',
      // eslint-disable-next-line react/display-name
      onRender: () => (
        <DateRangeSelector
          label={t('Start Date', {
            ns: 'search',
            defaultValue: 'Start Date',
          })}
          dateRangeEnd={DateRangeEnd.start}
          setDate={setStartDate}
          date={startDate}
          type={startType}
          setType={setStartType}
          isValid={isStartDateValid}
        />
      ),
    },
    {
      key: 'enddate',
      // eslint-disable-next-line react/display-name
      onRender: () => (
        <DateRangeSelector
          label={t('End Date', { ns: 'search', defaultValue: 'End Date' })}
          dateRangeEnd={DateRangeEnd.end}
          setDate={setEndDate}
          date={endDate}
          type={endType}
          setType={setEndType}
          isValid={isEndDateValid}
        />
      ),
    },
    { key: 'div', itemType: ContextualMenuItemType.Divider },
    {
      key: 'errorlist',
      // eslint-disable-next-line react/display-name
      onRender: () => (
        <PickerErrorBar
          {...{
            isEndBeforeStart,
            isAnyDateAfterToday,
            isMoreThanOneYear,
          }}
        />
      ),
    },
    {
      key: 'actions',
      // eslint-disable-next-line react/display-name
      onRender: (_item, dismissMenu) => {
        const itemSaveChanges = () => {
          onSave();
          // We pass "true" as a "dismiss all" so focus is returned to the
          // original button.
          dismissMenu(true);
        };
        return (
          <PickerActionBar
            {...{
              saveChanges: itemSaveChanges,
              hasChanges,
              isEndBeforeStart,
              isMoreThanOneYear,
              clear,
              resetToDefault,
            }}
          />
        );
      },
    },
  ];
};
